import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { push } from 'connected-react-router';

import {
  InitializeState,
  InitializeStatus
} from 'common/modules/app/initialize';
import { loadCookie, SasCookieKeys } from 'common/utils/cookie-utils';
import { ErrorState } from 'common/modules/app/errors';
import { GlossaryState } from 'common/modules/app/glossaries';
import { Glossary } from 'common/utils/glossary';

import { AppState } from 'pages';
import { ErrorPage } from '../components/ErrorPage';
import { FetchState, FetchStatus } from 'common/modules/app/fetch';
import { PlanInitialState } from 'common/modules/domain/plan';
import { LOGOUT_USER } from 'common/modules/domain/user';
import { boardState } from 'common/modules/app/board';
import {
  POST_PURCHASE_SESSION_ID,
  PurchaseStateType,
  setPurchaseState,
  PurchaseSessionState
} from 'common/modules/domain/purchaseSession';
import { getPlanListUrl, getPurchaseUrl } from 'common/utils/urls';
import { UserInformation, getLogout } from 'common/modules/domain/user';
import {
  PurchaseStatus,
  setStatus as setPurchaseStatus
} from 'common/modules/app/purchase';

/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const initState = useSelector<AppState, InitializeState>(
    (state) => state.app.initialize
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const errorState = useSelector<AppState, ErrorState>(
    (state) => state.app.error
  );
  const planState = useSelector<AppState, PlanInitialState>(
    (state) => state.domain.plan
  );
  const fetchState = useSelector<AppState, FetchState>(
    (state) => state.app.fetch
  );
  const boardState = useSelector<AppState, boardState>(
    (state) => state.app.board
  );
  const purchaseSessionState = useSelector<AppState, PurchaseSessionState>(
    (state) => state.domain.purchaseSession
  );
  const userState = useSelector<AppState, UserInformation>(
    (state) => state.domain.user
  );
  const targetPlan = planState.planList[boardState.selectedPlanId];
  const fetchProcess = fetchState[POST_PURCHASE_SESSION_ID];
  const logoutProccess = fetchState[LOGOUT_USER];

  return {
    init: initState,
    glossary: Glossary.instance(glState),
    errorType: errorState.type,
    errorMessage: errorState.message,
    targetPlan: targetPlan,
    country: userState.country,
    language: userState.language,
    purchaseSession: purchaseSessionState,
    fetchProcess: fetchProcess,
    logoutProccess: logoutProccess
  };
};

type SelectedState = ReturnType<typeof useStateProps>;
// Actionをdispatchする関数からComponentに渡すPropsへ
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();

  /** 初期化処理 */
  const init = () => {
    window.scrollTo(0, 0);
  };

  /** プラン一覧への遷移処理 */
  const toMovePlanList = () => {
    dispatch(push(getPlanListUrl()));
  };

  /** 契約中への遷移処理 */
  const toMoveContractList = () => {
    // 既に履歴画面が表示されていた場合に備えてリセットしておく
    dispatch(setPurchaseStatus(PurchaseStatus.CONTRACT_DISP));
    dispatch(push(getPurchaseUrl()));
  };

  /** ログアウトへの遷移処理 */
  const toMoveLogout = () => {
    // ログアウト実施中の場合は何もしない
    if (state.logoutProccess !== FetchStatus.PROCESSING) {
      const cookie = loadCookie();
      dispatch(getLogout(cookie[SasCookieKeys.KEY_TOKEN]));
    }
  };

  /** 購入するへの遷移処理 */
  const toMoveBuy = async () => {
    // PurchaseStateのStatusを更新し、決済へ進むようにする
    if (
      state.purchaseSession.state !== PurchaseStateType.TYPE_DISPLAY_PAYMENT
    ) {
      await dispatch(setPurchaseState(PurchaseStateType.TYPE_DISPLAY_PAYMENT));
    }
  };

  // 利用量制限のあるプランの多重購入エラーの場合、上限と現在回数が入ってくるので、画面表示のため渡す
  let limit;
  let usage;
  let name;
  if (state.purchaseSession.limit) {
    limit = state.purchaseSession.limit.toString();
  }
  if (state.purchaseSession.usage != null && state.purchaseSession.usage >= 0) {
    usage = state.purchaseSession.usage.toString();
  }
  // 現状はtargetPlanが合って重複購入不可の場合に名前を保持するようにした。渡せてると思うけどテストできてない
  if (
    state.targetPlan != null &&
    state.purchaseSession.state == 'duplicate_purchased'
  ) {
    name = state.targetPlan.name;
  }

  return {
    planUsage: usage,
    planLimit: limit,
    planName: name,
    movePlanList: toMovePlanList,
    moveContractList: toMoveContractList,
    moveLogOut: toMoveLogout,
    moveBuy: toMoveBuy,
    initialize: init
  };
};

/**
 * エラー画面のContainer Component
 */
export default function ContainerComponent() {
  const _props = { ...useStateProps(), ...useMargeProps(useStateProps()) };
  const isCompleteInit = _props.init.status === InitializeStatus.FINISH;

  React.useEffect(() => {
    _props.initialize();
  }, []);

  return isCompleteInit ? <ErrorPage {..._props} /> : null;
}
